import * as Sentry from '@sentry/vue';

const SENSITIVE_KEY_PATTERN =
  /(authorization|token|secret|password|cookie|api[-_]?key|credential|phone|mobile|whatsapp|prompt|completion|payload|webhook|headers?)/i;
const SENSITIVE_QUERY_PATTERN =
  /(token|secret|password|key|auth|signature|credential)/i;
const HIGH_RISK_CONTENT_KEY_PATTERN =
  /(^|_)(body|content|message|prompt|response|text|transcript|payload|webhook)(_|$)/i;
const MAX_STRING_LENGTH = 800;

const truncateString = value =>
  value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}…`
    : value;

function sanitizeUrl(value) {
  if (!value) {
    return value;
  }

  try {
    const url = new URL(value, window.location.origin);
    Array.from(url.searchParams.keys()).forEach(key => {
      if (SENSITIVE_QUERY_PATTERN.test(key)) {
        url.searchParams.set(key, '[redacted]');
      }
    });
    return url.toString();
  } catch {
    return truncateString(value);
  }
}

function sanitizeValue(value, depth = 0) {
  if (value === null || value === undefined) {
    return value;
  }

  if (depth > 4) {
    return '[truncated]';
  }

  if (typeof value === 'string') {
    if (/^https?:\/\//i.test(value)) {
      return sanitizeUrl(value);
    }
    return truncateString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: truncateString(value.message),
      stack: value.stack ? truncateString(value.stack) : undefined,
    };
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map(item => sanitizeValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value).slice(0, 40);
    return Object.fromEntries(
      entries.map(([key, entryValue]) => {
        if (
          SENSITIVE_KEY_PATTERN.test(key) ||
          HIGH_RISK_CONTENT_KEY_PATTERN.test(key)
        ) {
          return [key, '[redacted]'];
        }

        return [key, sanitizeValue(entryValue, depth + 1)];
      })
    );
  }

  return String(value);
}

function resolveTracesSampleRate(rawValue) {
  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return undefined;
  }

  return Math.min(1, Math.max(0, numericValue));
}

function normalizeErrorLoggingConfig(rawConfig) {
  if (typeof rawConfig === 'string') {
    const dsn = rawConfig.trim();
    return dsn ? { dsn } : null;
  }

  if (!rawConfig || typeof rawConfig !== 'object') {
    return null;
  }

  const dsn = String(rawConfig.dsn || '').trim();
  if (!dsn) {
    return null;
  }

  return {
    dsn,
    environment: String(rawConfig.environment || '').trim() || undefined,
    release: String(rawConfig.release || '').trim() || undefined,
    tracesSampleRate: resolveTracesSampleRate(rawConfig.tracesSampleRate),
    service: String(rawConfig.service || '').trim() || undefined,
    module: String(rawConfig.module || '').trim() || undefined,
  };
}

function sanitizeEvent(event, hint) {
  const nextEvent = {
    ...event,
    request: event.request
      ? {
          ...event.request,
          headers: undefined,
          cookies: undefined,
          data: event.request.data
            ? sanitizeValue(event.request.data)
            : undefined,
          url: event.request.url
            ? sanitizeUrl(event.request.url)
            : event.request.url,
        }
      : event.request,
    extra: event.extra ? sanitizeValue(event.extra) : event.extra,
    contexts: event.contexts ? sanitizeValue(event.contexts) : event.contexts,
    user: event.user
      ? {
          ...event.user,
          email: undefined,
          ip_address: undefined,
          username: undefined,
        }
      : event.user,
    breadcrumbs: event.breadcrumbs?.map(breadcrumb => ({
      ...breadcrumb,
      data: breadcrumb.data ? sanitizeValue(breadcrumb.data) : breadcrumb.data,
      message: breadcrumb.message
        ? String(sanitizeValue(breadcrumb.message))
        : breadcrumb.message,
    })),
  };

  if (hint?.originalException) {
    nextEvent.extra = {
      ...(nextEvent.extra || {}),
      originalException: sanitizeValue(hint.originalException),
    };
  }

  return nextEvent;
}

export function initChatwootFrontendSentry({ app, router, moduleName }) {
  try {
    const config = normalizeErrorLoggingConfig(window.errorLoggingConfig);
    if (!config) {
      return false;
    }

    Sentry.init({
      app,
      dsn: config.dsn,
      environment: config.environment || 'production',
      release: config.release,
      sendDefaultPii: false,
      integrations: config.tracesSampleRate
        ? [Sentry.browserTracingIntegration({ router })]
        : [],
      tracesSampleRate: config.tracesSampleRate,
      denyUrls: [
        /^chrome:\/\//i,
        /chrome-extension:/i,
        /extensions\//i,
        /file:\/\//i,
        /safari-web-extension:/i,
        /safari-extension:/i,
      ],
      ignoreErrors: [
        'ResizeObserver loop completed with undelivered notifications',
      ],
      beforeSend(event, hint) {
        return sanitizeEvent(event, hint);
      },
      beforeBreadcrumb(breadcrumb) {
        return {
          ...breadcrumb,
          data: breadcrumb.data
            ? sanitizeValue(breadcrumb.data)
            : breadcrumb.data,
          message: breadcrumb.message
            ? String(sanitizeValue(breadcrumb.message))
            : breadcrumb.message,
        };
      },
    });

    Sentry.setTag('service', config.service || 'goatendi-chatwoot-core');
    Sentry.setTag('runtime', 'vue');
    Sentry.setTag('module', moduleName || config.module || 'chatwoot-frontend');

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      '[observability] Failed to initialize Chatwoot frontend Sentry',
      error
    );
    return false;
  }
}
